
import React, { useEffect, useMemo } from 'react';
import { GenericScheduleProps, Schedule15TaxCreditData, ForeignTaxCreditItem, ForeignTaxCreditTaxGroupMemberItem, TaxGroupMemberItem, FormErrorsStep } from '../../types';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { RadioGroup } from '../ui/RadioGroup';
import { Button } from '../ui/Button'; 
import { translations } from '../../i18n/translations';
import { safeParseFloat } from '../../utils/calculations';


export const Schedule15TaxCredits: React.FC<GenericScheduleProps<Schedule15TaxCreditData>> = ({ scheduleData, onChange, errors, t, fullFormData }) => {

  const handleItemChange = (index: number, field: keyof ForeignTaxCreditItem, value: string) => {
    const updatedItems = scheduleData.foreignTaxCredits.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    );
    onChange('foreignTaxCredits', updatedItems);
  };

  const addItem = () => {
    onChange('foreignTaxCredits', [
      ...scheduleData.foreignTaxCredits,
      { id: Date.now().toString(), country: '', incomeSubjectToForeignTax: '', foreignTaxPaid: '', evidenceConfirmation: '', natureOfCredit: '' }
    ]);
  };

  const removeItem = (id: string) => {
    onChange('foreignTaxCredits', scheduleData.foreignTaxCredits.filter(item => item.id !== id));
  };
  
  const handleGroupItemChange = (index: number, field: keyof ForeignTaxCreditTaxGroupMemberItem, value: string) => {
    const updatedItems = scheduleData.foreignTaxCreditsForGroupMembers.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    onChange('foreignTaxCreditsForGroupMembers', updatedItems);
  };

  const addGroupItem = () => {
    onChange('foreignTaxCreditsForGroupMembers', [
      ...scheduleData.foreignTaxCreditsForGroupMembers,
      { id: Date.now().toString(), memberName: '', taxableIncomeAttributableToMember: '', country: '', incomeSubjectToForeignTax: '', foreignTaxPaid: '', evidenceConfirmation: '', natureOfCredit: '' }
    ]);
  };

  const removeGroupItem = (id: string) => {
    onChange('foreignTaxCreditsForGroupMembers', scheduleData.foreignTaxCreditsForGroupMembers.filter(item => item.id !== id));
  };
  
  const countryOptions = [ 
    { value: '', label: t('select_option') }, 
    { value: 'US', label: t('country_us') }, 
    { value: 'GB', label: t('country_gb') }, 
    { value: 'IN', label: t('country_in') }, 
    { value: 'AE', label: t('country_ae') },
    { value: 'SA', label: t('country_sa') },
    { value: 'EG', label: t('country_eg') },
  ];

  const taxGroupMembersOptions = useMemo(() => {
    const members = fullFormData.taxablePersonDetails.taxGroupSpecifics?.members || [];
    return [
      { value: '', label: t('select_option') },
      ...members.map((member: TaxGroupMemberItem) => ({ value: member.trn || member.id, label: member.name || `Member TRN: ${member.trn}` }))
    ];
  }, [fullFormData.taxablePersonDetails.taxGroupSpecifics?.members, t]);

  useEffect(() => {
    let totalClaimed = 0;
    const updatedFtcs = scheduleData.foreignTaxCredits.map(item => {
        const income = safeParseFloat(item.incomeSubjectToForeignTax);
        const foreignTax = safeParseFloat(item.foreignTaxPaid);
        const uaeTax = income * 0.09; 
        const credit = Math.min(foreignTax, uaeTax);
        totalClaimed += credit > 0 ? credit : 0;
        return {
            ...item,
            uaeTaxOnRelevantIncome: uaeTax.toFixed(2),
            taxCreditClaimed: credit.toFixed(2)
        };
    });

    const updatedGroupFtcs = scheduleData.foreignTaxCreditsForGroupMembers.map(item => {
        const income = safeParseFloat(item.taxableIncomeAttributableToMember); 
        const foreignTax = safeParseFloat(item.foreignTaxPaid);
        const uaeTaxOnMemberIncome = income * 0.09; 
        const credit = Math.min(foreignTax, uaeTaxOnMemberIncome);
        totalClaimed += credit > 0 ? credit : 0;
        return {
            ...item,
            uaeTaxOnRelevantIncome: uaeTaxOnMemberIncome.toFixed(2),
            taxCreditClaimed: credit.toFixed(2)
        };
    });
    
    let changed = false;
    if(JSON.stringify(updatedFtcs) !== JSON.stringify(scheduleData.foreignTaxCredits)){
        onChange('foreignTaxCredits', updatedFtcs);
        changed = true;
    }
    if(JSON.stringify(updatedGroupFtcs) !== JSON.stringify(scheduleData.foreignTaxCreditsForGroupMembers)){
        onChange('foreignTaxCreditsForGroupMembers', updatedGroupFtcs);
        changed = true;
    }

    if (scheduleData.totalTaxCreditsClaimed !== totalClaimed.toFixed(2)) {
        onChange('totalTaxCreditsClaimed', totalClaimed.toFixed(2));
    }
  }, [scheduleData.foreignTaxCredits, scheduleData.foreignTaxCreditsForGroupMembers, onChange, scheduleData.totalTaxCreditsClaimed]);


  return (
    <div className="space-y-4">
      <h3 className="text-lg font-bold text-sky-600">{t('schedule15_title')}</h3>
      
      <SectionHeader title={t('s15_foreign_tax_credits_title')} />
      {scheduleData.foreignTaxCredits.map((item, index) => (
        <div key={item.id} className="p-3 border rounded-md space-y-3 bg-slate-50 relative">
            <p className="text-sm font-semibold text-gray-600">{t('foreign_tax_credit_item_heading', {index: index + 1})}</p>
            <Select label={t('s15_ftc_item_country_label')} id={`ftcCountry-${item.id}`} value={item.country} options={countryOptions} onChange={(e) => handleItemChange(index, 'country', e.target.value)} error={(errors?.foreignTaxCredits as FormErrorsStep[] | undefined)?.[index]?.country as string} />
            <Input label={t('s15_ftc_item_income_foreign_tax_label')} type="number" id={`ftcIncome-${item.id}`} value={item.incomeSubjectToForeignTax} onChange={(e) => handleItemChange(index, 'incomeSubjectToForeignTax', e.target.value)} error={(errors?.foreignTaxCredits as FormErrorsStep[] | undefined)?.[index]?.incomeSubjectToForeignTax as string}/>
            <Input label={t('s15_ftc_item_foreign_tax_paid_label')} type="number" id={`ftcPaid-${item.id}`} value={item.foreignTaxPaid} onChange={(e) => handleItemChange(index, 'foreignTaxPaid', e.target.value)}  error={(errors?.foreignTaxCredits as FormErrorsStep[] | undefined)?.[index]?.foreignTaxPaid as string}/>
            <Input label={t('s15_ftc_item_nature_of_credit_label')} type="text" id={`ftcNature-${item.id}`} value={item.natureOfCredit} onChange={(e) => handleItemChange(index, 'natureOfCredit', e.target.value)} error={(errors?.foreignTaxCredits as FormErrorsStep[] | undefined)?.[index]?.natureOfCredit as string}/>
            <RadioGroup label={t('s15_ftc_item_evidence_confirmation_label')} name={`ftcEvidence-${item.id}`} value={item.evidenceConfirmation} onChange={(val) => handleItemChange(index, 'evidenceConfirmation', val)} options={[{label: t('yes'), value: 'yes'}, {label: t('no'), value: 'no'}]} t={t} error={(errors?.foreignTaxCredits as FormErrorsStep[] | undefined)?.[index]?.evidenceConfirmation as string}/>
            <Input label={t('s15_ftc_member_uae_tax_on_income_label')} type="text" id={`ftcUaeTax-${item.id}`} value={item.uaeTaxOnRelevantIncome || ''} readOnly className="bg-gray-100" />
            <Input label={t('s15_ftc_member_credit_claimed_label')} type="text" id={`ftcClaimed-${item.id}`} value={item.taxCreditClaimed || ''} readOnly className="bg-gray-100" />
            <Button onClick={() => removeItem(item.id)} variant="danger_outline" size="sm" className="absolute top-2 right-2">{t('remove_item_button')}</Button>
        </div>
      ))}
      <Button onClick={addItem} variant="secondary" size="sm">{t('add_item_button')}</Button>

      {fullFormData.taxablePersonDetails.filingAs === 'tax_group_parent' && (
        <>
            <SectionHeader title={t('s15_tax_group_attribution_title')} />
            {scheduleData.foreignTaxCreditsForGroupMembers.map((item, index) => (
                 <div key={item.id} className="p-3 border rounded-md space-y-3 bg-sky-50 relative">
                    <p className="text-sm font-semibold text-gray-600">{t('foreign_tax_credit_item_heading', {index: index + 1})} ({t('s15_tax_group_attribution_title')})</p>
                    <Select label={t('s15_ftc_member_name_label')} id={`ftcMemberName-${item.id}`} value={item.memberName} options={taxGroupMembersOptions} onChange={(e) => handleGroupItemChange(index, 'memberName', e.target.value)} error={(errors?.foreignTaxCreditsForGroupMembers as FormErrorsStep[] | undefined)?.[index]?.memberName as string} />
                    <Input label={t('s15_ftc_member_taxable_income_label')} type="number" id={`ftcMemberIncome-${item.id}`} value={item.taxableIncomeAttributableToMember} onChange={(e) => handleGroupItemChange(index, 'taxableIncomeAttributableToMember', e.target.value)} error={(errors?.foreignTaxCreditsForGroupMembers as FormErrorsStep[] | undefined)?.[index]?.taxableIncomeAttributableToMember as string}/>
                    <Select label={t('s15_ftc_item_country_label')} id={`ftcMemberCountry-${item.id}`} value={item.country} options={countryOptions} onChange={(e) => handleGroupItemChange(index, 'country', e.target.value)} error={(errors?.foreignTaxCreditsForGroupMembers as FormErrorsStep[] | undefined)?.[index]?.country as string} />
                    <Input label={t('s15_ftc_item_income_foreign_tax_label')} type="number" id={`ftcMemberForeignIncome-${item.id}`} value={item.incomeSubjectToForeignTax} onChange={(e) => handleGroupItemChange(index, 'incomeSubjectToForeignTax', e.target.value)} error={(errors?.foreignTaxCreditsForGroupMembers as FormErrorsStep[] | undefined)?.[index]?.incomeSubjectToForeignTax as string}/>
                    <Input label={t('s15_ftc_member_foreign_tax_paid_label')} type="number" id={`ftcMemberForeignTaxPaid-${item.id}`} value={item.foreignTaxPaid} onChange={(e) => handleGroupItemChange(index, 'foreignTaxPaid', e.target.value)}  error={(errors?.foreignTaxCreditsForGroupMembers as FormErrorsStep[] | undefined)?.[index]?.foreignTaxPaid as string}/>
                    <Input label={t('s15_ftc_item_nature_of_credit_label')} type="text" id={`ftcMemberNature-${item.id}`} value={item.natureOfCredit} onChange={(e) => handleGroupItemChange(index, 'natureOfCredit', e.target.value)} error={(errors?.foreignTaxCreditsForGroupMembers as FormErrorsStep[] | undefined)?.[index]?.natureOfCredit as string}/>
                    <RadioGroup label={t('s15_ftc_item_evidence_confirmation_label')} name={`ftcMemberEvidence-${item.id}`} value={item.evidenceConfirmation} onChange={(val) => handleGroupItemChange(index, 'evidenceConfirmation', val)} options={[{label: t('yes'), value: 'yes'}, {label: t('no'), value: 'no'}]} t={t} error={(errors?.foreignTaxCreditsForGroupMembers as FormErrorsStep[] | undefined)?.[index]?.evidenceConfirmation as string}/>
                    <Input label={t('s15_ftc_member_uae_tax_on_income_label')} type="text" id={`ftcMemberUaeTax-${item.id}`} value={item.uaeTaxOnRelevantIncome || ''} readOnly className="bg-gray-100" />
                    <Input label={t('s15_ftc_member_credit_claimed_label')} type="text" id={`ftcMemberClaimed-${item.id}`} value={item.taxCreditClaimed || ''} readOnly className="bg-gray-100" />
                    <Button onClick={() => removeGroupItem(item.id)} variant="danger_outline" size="sm" className="absolute top-2 right-2">{t('remove_item_button')}</Button>
                 </div>
            ))}
            <Button onClick={addGroupItem} variant="secondary" size="sm">{t('s15_add_group_member_ftc_button')}</Button>
        </>
      )}

      <Input label={t('s15_total_credits_claimed_label')} type="text" id="totalTaxCreditsClaimed" value={scheduleData.totalTaxCreditsClaimed || '0.00'} readOnly className="bg-gray-100 font-semibold" />
    </div>
  );
};

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <h4 className="text-md font-semibold text-sky-700 mt-4 mb-2 border-b pb-1">{title}</h4>
);
